import { safeAreaTop } from '@/utils';
import { Flex, Space, Text, Title } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import {
	AdvancedMarker,
	AdvancedMarkerAnchorPoint,
	APIProvider,
	Map,
	Pin,
} from '@vis.gl/react-google-maps';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';

import { useDropOffLocationsQuery } from './graphql.generated';
import styles from './styles.module.css';

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

type LocationItem = {
	__typename?: 'DropOffLocation';
	address: string;
	lat: number;
	lng: number;
	name: string;
	retailer?: string | null;
	suburb: string;
};

export const DropPointsMap = ({
	open,
	setOpen,
}: {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
	const { data } = useDropOffLocationsQuery();
	const [selectedLocation, setSelectedLocation] = useState<LocationItem | null>(null);
	const notchPadding = safeAreaTop();

	// Create a stable mapId that doesn't change on every render
	const mapId = useMemo(() => `drop-points-map-${Date.now()}`, []);

	const selectLocation = (loc: LocationItem | null) => {
		setSelectedLocation((prev) => {
			if (prev?.address === loc?.address) {
				return null;
			}
			return loc;
		});
	};

	// Compute display name for selected location
	const selectedLocationDisplayName = selectedLocation?.retailer
		? `${selectedLocation.retailer} - ${selectedLocation.name}`
		: (selectedLocation?.name ?? '');

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ y: '100vh' }}
					animate={{ y: 0 }}
					exit={{ y: '100vh' }}
					transition={{ duration: 0.2 }}
					className={styles.mapContainer}
					key="drop-points-map"
				>
					<div className={styles.header}>
						<Space h={notchPadding} />
						<Flex justify="space-between" align="center">
							<Title size="h3">Drop off points</Title>
							<IconX onClick={() => setOpen(false)} size={24} />
						</Flex>
					</div>
					{!API_KEY ? (
						<div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
							Google Maps API Key is missing. Please add VITE_GOOGLE_MAPS_API_KEY to your .env.local
							file.
						</div>
					) : (
						<div className={styles.mapWrapper}>
							{data?.currentUser && (
								<APIProvider apiKey={API_KEY}>
									<Map
										defaultZoom={11}
										disableDefaultUI
										gestureHandling="greedy"
										mapId={mapId}
										onClick={() => selectedLocation && setSelectedLocation(null)}
										defaultCenter={{
											lat: data?.currentUser?.lat ?? -33.8688,
											lng: data?.currentUser?.lng ?? 151.2093,
										}}
										style={{ width: '100%', height: '100%' }}
									>
										{data?.dropOffLocations?.map((loc) => (
											<AdvancedMarker
												key={`${loc?.address}-${loc?.lat}-${loc?.lng}`}
												onClick={() => selectLocation(loc)}
												className="custom-marker"
												style={{
													transformOrigin: AdvancedMarkerAnchorPoint['BOTTOM'].join(' '),
												}}
												position={{
													lat: loc?.lat ?? 0,
													lng: loc?.lng ?? 0,
												}}
											>
												<Pin
													background={
														selectedLocation?.address === loc?.address ? '#ff6b35' : '#22ccff'
													}
													borderColor={
														selectedLocation?.address === loc?.address ? '#d54e21' : '#1e89a1'
													}
													glyphColor={
														selectedLocation?.address === loc?.address ? '#a83c1a' : '#0f677a'
													}
												/>
											</AdvancedMarker>
										))}

										{/* User Location Marker */}
										{data?.currentUser?.lat && data?.currentUser?.lng && (
											<AdvancedMarker
												position={{
													lat: data.currentUser.lat,
													lng: data.currentUser.lng,
												}}
												style={{
													transformOrigin: AdvancedMarkerAnchorPoint['BOTTOM'].join(' '),
												}}
											>
												<Pin
													background={'#4caf50'}
													borderColor={'#388e3c'}
													glyphColor={'#2e7d32'}
													scale={1.1}
												/>
											</AdvancedMarker>
										)}
									</Map>
								</APIProvider>
							)}
						</div>
					)}

					{/* Location Details Drawer */}
					<AnimatePresence>
						{selectedLocation && (
							<motion.div
								initial={{ y: '100%' }}
								animate={{ y: 0 }}
								exit={{ y: '100%' }}
								transition={{ duration: 0.3, ease: 'easeInOut' }}
								className={styles.locationDrawer}
								drag="y"
								dragConstraints={{ top: 0, bottom: 0 }}
								dragElastic={0.2}
								onDragEnd={(_, info) => {
									// Close drawer if dragged down more than 100px or with enough velocity
									if (info.offset.y > 100 || info.velocity.y > 500) {
										setSelectedLocation(null);
									}
								}}
								whileDrag={{
									transition: { duration: 0 },
								}}
							>
								<div className={styles.drawerHandle} />
								<div className={styles.drawerContent}>
									{(() => {
										return (
											<Flex justify="space-between" align="flex-start" mb="md">
												<Text size="lg" fw={600}>
													{selectedLocationDisplayName}
												</Text>
												<IconX
													onClick={() => setSelectedLocation(null)}
													size={20}
													style={{ cursor: 'pointer', color: '#666' }}
												/>
											</Flex>
										);
									})()}

									<Flex direction="column" gap="xs">
										<Text size="sm" c="dimmed">
											Address
										</Text>
										<Text size="sm" mb="sm">
											{selectedLocation.address}, {selectedLocation.suburb}
										</Text>
									</Flex>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</motion.div>
			)}
		</AnimatePresence>
	);
};
